/* eslint-disable max-lines */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import type { TFunction } from "i18next"

import {
  downloadExtractionDocx,
  downloadPmDocx,
  type TenderCaseListItemApi,
} from "@/api/tender-api"
import {
  AlarmClock,
  AlertTriangle,
  ArrowRightLeft,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Clock,
  FileDown,
  FileText,
  Mail,
  MoreHorizontal,
  Plus,
  Scale,
  Search,
  Shield,
  Trash2,
  Users,
  X,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { SidebarInset } from "@/components/ui/sidebar"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useTenderAdminModeStore } from "@/stores/tender-admin-mode-store"

import { QuickStartModal } from "./quick-start-modal"
import { TenderAdminModeToggle } from "./tender-admin-mode-toggle"
import { TenderHandoffDialog } from "./tender-handoff-dialog"
import {
  type DerivedStatus,
  deriveListItemStatus,
  isActiveAutoPipelineStatus,
  isInPreparation,
  isProcessingStepStatus,
  STATUS_CONFIG,
} from "./tender-status-utils"
import {
  TenderCasesDeleteError,
  useDeleteTenderCase,
  useDeleteTenderCases,
  useTenderCases,
} from "../hooks/use-tender-cases"
import { useTenderPaths } from "../hooks/use-tender-paths"
import { deadlineDaysLeft, deadlineTime, deadlineToDate } from "../utils/deadline"
import { isHandedOff } from "../utils/is-handed-off"
import { isInvolvedCase } from "../utils/is-involved-case"

// ── Cell helpers ───────────────────────────────────────────────────────────────

function deadlineUrgency(deadline: string): "past" | "urgent" | "warning" | "normal" {
  if (deadlineToDate(deadline).getTime() < Date.now()) return "past"
  const daysLeft = deadlineDaysLeft(deadline)
  if (daysLeft <= 7) return "urgent"
  if (daysLeft <= 21) return "warning"
  return "normal"
}

function DeadlineCell({ deadline }: { deadline?: string | null }) {
  const { t, i18n } = useTranslation()
  if (!deadline) return <span className="text-muted-foreground text-xs">—</span>
  if (deadlineTime(deadline) === null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-warning text-xs whitespace-nowrap">
            {t("tender.landing.deadlineUnclear")}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-72 text-xs">{deadline}</TooltipContent>
      </Tooltip>
    )
  }

  const due = deadlineToDate(deadline)
  const daysLeft = deadlineDaysLeft(deadline)
  const urgency = deadlineUrgency(deadline)

  const formattedDate = due.toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  if (urgency === "past") {
    return (
      <span className="text-muted-foreground/60 text-xs tabular-nums line-through">
        {formattedDate}
      </span>
    )
  }

  if (urgency === "urgent") {
    return (
      <span className="bg-destructive/10 text-destructive ring-destructive/20 inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ring-1">
        <AlarmClock className="size-3 shrink-0" />
        {daysLeft === 0
          ? t("tender.landing.deadlineToday")
          : t("tender.landing.daysLeft", { count: daysLeft })}
      </span>
    )
  }

  if (urgency === "warning") {
    return (
      <span className="bg-warning/10 text-warning ring-warning/20 inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ring-1">
        <Clock className="size-3 shrink-0" />
        {t("tender.landing.daysLeft", { count: daysLeft })}
      </span>
    )
  }

  return <span className="text-muted-foreground text-xs tabular-nums">{formattedDate}</span>
}

function LastActivityCell({ timestamp }: { timestamp: string }) {
  const { t, i18n } = useTranslation()
  const now = new Date()
  const ts = new Date(timestamp)
  const diffMs = now.getTime() - ts.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (diffHours === 0) {
      const diffMin = Math.floor(diffMs / (1000 * 60))
      return (
        <span className="text-muted-foreground text-xs">
          {diffMin <= 1
            ? t("tender.landing.activity.justNow")
            : t("tender.landing.activity.minutesAgo", { count: diffMin })}
        </span>
      )
    }
    return (
      <span className="text-muted-foreground text-xs">
        {t("tender.landing.activity.hoursAgo", { count: diffHours })}
      </span>
    )
  }
  if (diffDays === 1) {
    return <span className="text-muted-foreground text-xs">{t("tender.landing.yesterday")}</span>
  }
  if (diffDays < 7) {
    return (
      <span className="text-muted-foreground text-xs">
        {t("tender.landing.activity.daysAgo", { count: diffDays })}
      </span>
    )
  }
  return (
    <span className="text-muted-foreground text-xs tabular-nums">
      {ts.toLocaleDateString(i18n.language, { month: "short", day: "numeric" })}
    </span>
  )
}

// ── Sorting ────────────────────────────────────────────────────────────────────

type SortKey = "title" | "deadline" | "createdAt" | "activity"
type SortDir = "asc" | "desc"
type OwnerFilter = "__all__" | "__mine__" | string

const OWNER_FILTER_ALL = "__all__"
const OWNER_FILTER_MINE = "__mine__"

// ── Status filter ────────────────────────────────────────────────────────────
// Collapses the granular DerivedStatus values into the product workflow stages
// shown in the landing-page status dropdown.
type StatusFilter =
  | "__all__"
  | "setup"
  | "analysing"
  | "extraction"
  | "cv_matching"
  | "bid_review"
  | "go"
  | "no_go"

const STATUS_FILTER_ALL: StatusFilter = "__all__"

// Each bucket lists the DerivedStatus values it matches, plus the i18n label key
// reused from tender.status.derived.* (so we don't duplicate stage names).
const STATUS_FILTER_BUCKETS: { value: StatusFilter; labelKey: string; matches: DerivedStatus[] }[] =
  [
    { value: "setup", labelKey: "tender.status.derived.setup", matches: ["setup"] },
    {
      value: "analysing",
      labelKey: "tender.status.derived.analyserar",
      matches: [
        "analyserar",
        "analyserar_uploading",
        "analyserar_indexing",
        "analyserar_extracting",
        "analyserar_cv_classifying",
        "analyserar_cv_matching",
      ],
    },
    {
      value: "extraction",
      labelKey: "tender.status.derived.extrahering",
      matches: ["extrahering", "klar_overlamning"],
    },
    {
      value: "cv_matching",
      labelKey: "tender.status.derived.cv_matchning",
      matches: ["cv_matchning"],
    },
    {
      value: "bid_review",
      labelKey: "tender.status.derived.anbudsgranskning",
      matches: ["anbudsgranskning"],
    },
    { value: "go", labelKey: "tender.status.derived.go", matches: ["go"] },
    { value: "no_go", labelKey: "tender.status.derived.no_go", matches: ["no_go"] },
  ]

function matchesStatusFilter(item: TenderCaseListItemApi, filter: StatusFilter): boolean {
  if (filter === STATUS_FILTER_ALL) return true
  const bucket = STATUS_FILTER_BUCKETS.find((b) => b.value === filter)
  return bucket ? bucket.matches.includes(deriveListItemStatus(item)) : true
}

// ── Handoff filter ───────────────────────────────────────────────────────────
// In preparation (not yet handed off) vs handed off. Sits alongside the stage
// filter so people can find "cases I'm still preparing" vs "handed to me".
type HandoffFilter = "__all__" | "in_prep" | "handed_off"
const HANDOFF_FILTER_ALL: HandoffFilter = "__all__"
const HANDOFF_FILTERS: { value: HandoffFilter; labelKey: string }[] = [
  { value: "in_prep", labelKey: "tender.landing.handoffInPrep" },
  { value: "handed_off", labelKey: "tender.landing.handoffHandedOff" },
]
// The phase split is the primary view switch (preparer vs picking-up), so it's
// rendered as a prominent segmented control rather than a peer dropdown.
const HANDOFF_SEGMENTS: { value: HandoffFilter; labelKey: string }[] = [
  { value: HANDOFF_FILTER_ALL, labelKey: "tender.landing.handoffAll" },
  ...HANDOFF_FILTERS,
]

// ── Persisted filter selections ──────────────────────────────────────────────
// Remember each user's chosen landing-page view (mine/all, stage, and handoff
// state) in localStorage so they return to the same filtered list next time.
const FILTER_STORAGE_KEYS = {
  owner: "tender-landing-owner-filter",
  status: "tender-landing-status-filter",
  handoff: "tender-landing-handoff-filter",
} as const

function readStoredFilter(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStoredFilter(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore (e.g. storage disabled)
  }
}

// Only the mine/all sentinels are persisted — a specific admin-picked owner name
// is transient and may not exist next session.
function initialOwnerFilter(): OwnerFilter {
  const stored = readStoredFilter(FILTER_STORAGE_KEYS.owner)
  return stored === OWNER_FILTER_ALL ? OWNER_FILTER_ALL : OWNER_FILTER_MINE
}

function initialStatusFilter(): StatusFilter {
  const stored = readStoredFilter(FILTER_STORAGE_KEYS.status)
  if (stored === STATUS_FILTER_ALL) return STATUS_FILTER_ALL
  return STATUS_FILTER_BUCKETS.some((b) => b.value === stored)
    ? (stored as StatusFilter)
    : STATUS_FILTER_ALL
}

function initialHandoffFilter(): HandoffFilter {
  const stored = readStoredFilter(FILTER_STORAGE_KEYS.handoff)
  return stored === "in_prep" || stored === "handed_off" ? stored : HANDOFF_FILTER_ALL
}

function getOwnerDisplayName(item: TenderCaseListItemApi, unknownLabel: string): string {
  const ownerName = item.ownerDisplayName?.trim()
  return ownerName || unknownLabel
}

function hasStoredOwnerAccess(tenderCase: TenderCaseListItemApi): boolean {
  if (tenderCase.currentUserAccessSource != null) {
    return tenderCase.currentUserAccessSource === "owner"
  }

  // The API always emits currentUserAccessSource (get_case_access_source
  // resolves a concrete value for every authenticated caller), so reaching
  // this fallback means version skew or a stale cache — fail closed rather
  // than granting owner affordances to open-read viewers with no role.
  return tenderCase.currentUserRole === "owner"
}

function canManageCase(tenderCase: TenderCaseListItemApi): boolean {
  return tenderCase.currentUserRole === "owner" || tenderCase.currentUserRole === "admin"
}

function canDeleteCase(tenderCase: TenderCaseListItemApi): boolean {
  // Mirrors the backend delete rule: stored owners and tender admins
  // (admin_default access) may delete; open viewers and readers may not.
  return (
    (hasStoredOwnerAccess(tenderCase) || tenderCase.currentUserAccessSource === "admin_default") &&
    tenderCase.currentUserRole !== "reader"
  )
}

function ReaderAccessIcon({ tenderCase }: { tenderCase: TenderCaseListItemApi }) {
  if (tenderCase.currentUserAccessSource === "admin_default") {
    return <Shield className="text-warning size-2.5" />
  }

  return <Users className="size-2.5" />
}

function isProcessing(item: TenderCaseListItemApi): boolean {
  return (
    isActiveAutoPipelineStatus(item.autoPipelineStatus) ||
    isProcessingStepStatus(item.extractionStatus) ||
    isProcessingStepStatus(item.cvMatchStatus) ||
    isProcessingStepStatus(item.bidReviewStatus)
  )
}

function isExtractionComplete(item: TenderCaseListItemApi): boolean {
  return item.extractionStatus === "completed"
}

type FilterCasesOptions = {
  getDerivedStatusLabel: (item: TenderCaseListItemApi) => string
  unknownOwnerLabel: string
}

function filterCases(
  cases: TenderCaseListItemApi[],
  query: string,
  { getDerivedStatusLabel, unknownOwnerLabel }: FilterCasesOptions
): TenderCaseListItemApi[] {
  const q = query.trim().toLowerCase()
  if (!q) return cases
  return cases.filter((c) => {
    const statusSearch = getDerivedStatusLabel(c).toLowerCase()
    const ownerLabel = getOwnerDisplayName(c, unknownOwnerLabel).toLowerCase()
    return c.title.toLowerCase().includes(q) || statusSearch.includes(q) || ownerLabel.includes(q)
  })
}

type SortCasesOptions = {
  key: SortKey
  dir: SortDir
  locale: string
}

function sortCases(
  cases: TenderCaseListItemApi[],
  { key, dir, locale }: SortCasesOptions
): TenderCaseListItemApi[] {
  const multiplier = dir === "asc" ? 1 : -1
  return [...cases].sort((a, b) => {
    switch (key) {
      case "title":
        return multiplier * a.title.localeCompare(b.title, locale)
      case "createdAt":
        return multiplier * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      case "activity":
        return (
          multiplier * (new Date(a.lastActivityAt).getTime() - new Date(b.lastActivityAt).getTime())
        )
      case "deadline": {
        const aMs = deadlineTime(a.submissionDeadline) ?? Infinity
        const bMs = deadlineTime(b.submissionDeadline) ?? Infinity
        return multiplier * (aMs - bMs)
      }
    }
  })
}

// ── Shared header ──────────────────────────────────────────────────────────────

type SortableHeaderProps = {
  label: string
  sortKey: SortKey
  active: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
  className?: string
}

function SortableHeader({ label, sortKey, active, dir, onSort, className }: SortableHeaderProps) {
  const isActive = active === sortKey
  return (
    <TableHead className={cn("px-4 py-2.5", className)}>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          "group h-auto gap-1 p-0 text-xs font-medium tracking-wide uppercase",
          isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
        )}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span
          className={cn(
            "transition-opacity",
            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-50"
          )}
        >
          {(() => {
            if (isActive && dir === "asc") return <ChevronUp className="size-3" />
            if (isActive && dir === "desc") return <ChevronDown className="size-3" />
            return <ChevronsUpDown className="size-3" />
          })()}
        </span>
      </Button>
    </TableHead>
  )
}

// ── Row menu ──────────────────────────────────────────────────────────────────

function CaseRowMenu({
  tenderCase,
  onDelete,
}: {
  tenderCase: TenderCaseListItemApi
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const showDownload = isExtractionComplete(tenderCase)
  const canDelete = canDeleteCase(tenderCase)

  if (!showDownload && !canDelete) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={t("tender.landing.rowActionsAria")}
          className="text-muted-foreground/60 hover:text-foreground size-7 rounded p-0"
          size="icon"
          type="button"
          variant="ghost"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {showDownload && (
          <DropdownMenuItem
            onClick={() =>
              void downloadExtractionDocx(tenderCase.id, tenderCase.activeRunId, tenderCase.title)
            }
          >
            <FileDown className="mr-2 size-4" />
            {t("tender.landing.downloadDocx")}
          </DropdownMenuItem>
        )}
        {canDelete && showDownload && <DropdownMenuSeparator />}
        {canDelete && (
          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
            <Trash2 className="mr-2 size-4" />
            {t("tender.landing.remove")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Row ────────────────────────────────────────────────────────────────────────

function listItemParticipantRoleLabel(t: TFunction, tenderCase: TenderCaseListItemApi): string {
  if (tenderCase.currentUserAccessSource === "admin_default") {
    return t("tender.roles.admin")
  }
  const role = tenderCase.currentUserRole
  if (role === "owner" || role === "admin" || role === "editor" || role === "reader") {
    return t(`tender.roles.${role}`)
  }
  return ""
}

type CaseRowProps = {
  tenderCase: TenderCaseListItemApi
  isSelected: boolean
  isSelectionMode: boolean
  showOwnerColumn: boolean
  showHandoffBadge: boolean
  onClick: (id: string) => void
  onDelete: (id: string) => void
  onToggle: () => void
}

function CaseRow({
  tenderCase,
  isSelected,
  isSelectionMode,
  showOwnerColumn,
  showHandoffBadge,
  onClick,
  onDelete,
  onToggle,
}: CaseRowProps) {
  const { t, i18n } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [handoffOpen, setHandoffOpen] = useState(false)
  const [failedWarningOpen, setFailedWarningOpen] = useState(false)
  const processing = isProcessing(tenderCase)
  const handoffReady = isExtractionComplete(tenderCase)
  const showHandoffAction = canManageCase(tenderCase)
  const handoffLabel = t(
    isHandedOff(tenderCase) ? "tender.summaryEmail.title" : "tender.header.handoff"
  )
  const statusConf = STATUS_CONFIG[deriveListItemStatus(tenderCase)]
  const { hasUnviewedNotification, invitationNotification } = tenderCase
  const { invitedByDisplayName, role } = invitationNotification ?? {}

  const row = (
    <TableRow
      className={cn(
        "group hover:bg-accent/40 cursor-pointer border-b transition-colors duration-150 last:border-0",
        isSelected && "bg-primary/5",
        processing && "border-l-accent border-l-2"
      )}
      onClick={() => onClick(tenderCase.id)}
    >
      <TableCell
        className="w-10 px-3 py-3.5"
        onClick={(e) => {
          e.stopPropagation()
          onToggle()
        }}
      >
        <div
          className={cn(
            "flex items-center justify-center transition-opacity duration-150",
            isSelected || isSelectionMode ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
        >
          <Checkbox
            checked={isSelected}
            className="cursor-pointer"
            onCheckedChange={() => onToggle()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </TableCell>
      <TableCell className="px-5 py-3.5">
        <div className="flex items-center gap-2">
          <span className="text-sm leading-snug font-medium">{tenderCase.title}</span>
          {showHandoffBadge && isHandedOff(tenderCase) && (
            <span className="text-muted-foreground bg-muted inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium">
              <ArrowRightLeft className="size-2.5" />
              {t("tender.header.handedOff")}
            </span>
          )}
          {tenderCase.currentUserAccessSource !== "owner" && tenderCase.currentUserRole && (
            <span className="text-muted-foreground inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]">
              <ReaderAccessIcon tenderCase={tenderCase} />
              {listItemParticipantRoleLabel(t, tenderCase)}
            </span>
          )}
          {hasUnviewedNotification && (
            <span className="bg-primary inline-block size-2 shrink-0 rounded-full" />
          )}
        </div>
      </TableCell>
      {showOwnerColumn && (
        <TableCell className="px-4 py-3.5">
          <span className="text-muted-foreground text-xs">
            {getOwnerDisplayName(tenderCase, t("tender.landing.unknownOwner"))}
          </span>
        </TableCell>
      )}
      <TableCell className="px-4 py-3.5">
        <div className="flex items-center gap-1.5">
          <Badge
            className={cn("border text-xs font-normal", statusConf.className)}
            variant="outline"
          >
            {t(statusConf.labelKey)}
          </Badge>
          {processing && <Spinner className="text-accent size-3 shrink-0" />}
          {tenderCase.hasFailedSubcategories && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle
                  aria-label={t("tender.landing.failedSubcategoriesWarning")}
                  className="text-warning size-3.5 shrink-0"
                />
              </TooltipTrigger>
              <TooltipContent>{t("tender.landing.failedSubcategoriesWarning")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <LastActivityCell timestamp={tenderCase.lastActivityAt} />
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <FileText className="size-3.5 shrink-0" />
          {tenderCase.documentCount}
        </span>
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <DeadlineCell deadline={tenderCase.submissionDeadline} />
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <span className="text-muted-foreground text-xs tabular-nums">
          {new Date(tenderCase.createdAt).toLocaleDateString(i18n.language, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
      </TableCell>
      <TableCell className="px-4 py-3.5 text-right">
        <div className="ml-auto flex items-center justify-end gap-1.5">
          {isExtractionComplete(tenderCase) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={t("tender.landing.downloadPmDocx")}
                  className="text-muted-foreground/60 hover:text-success size-7 rounded p-0"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    void downloadPmDocx(
                      tenderCase.id,
                      tenderCase.activeRunId,
                      tenderCase.title
                    ).catch(() => {
                      /* PM content may not exist for legacy runs — silently skip */
                    })
                  }}
                >
                  <FileDown className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("tender.landing.downloadPmDocx")}</TooltipContent>
            </Tooltip>
          )}
          {showHandoffAction && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    aria-label={handoffLabel}
                    disabled={!handoffReady}
                    size="icon"
                    type="button"
                    variant="ghost"
                    className={cn(
                      "size-7 rounded p-0",
                      handoffReady
                        ? "text-primary hover:text-primary"
                        : "text-muted-foreground/60 hover:text-foreground"
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!handoffReady) return
                      if (tenderCase.hasFailedSubcategories) {
                        setFailedWarningOpen(true)
                        return
                      }
                      setHandoffOpen(true)
                    }}
                  >
                    <Mail className="size-3.5" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {handoffReady ? handoffLabel : t("tender.landing.handoffDisabledSummaryNotReady")}
              </TooltipContent>
            </Tooltip>
          )}
          <CaseRowMenu tenderCase={tenderCase} onDelete={() => setConfirmOpen(true)} />
        </div>
      </TableCell>
    </TableRow>
  )

  return (
    <>
      {hasUnviewedNotification ? (
        <Tooltip>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent className="max-w-56 text-xs">
            <p className="font-medium">{t("tender.sidebar.notificationTitle")}</p>
            {invitedByDisplayName && (
              <p className="text-muted-foreground mt-0.5">
                {role === "owner" || role === "admin" || role === "editor" || role === "reader"
                  ? t("tender.sidebar.notificationInvitedByRole", {
                      name: invitedByDisplayName,
                      role: t(`tender.roles.${role}`),
                    })
                  : t("tender.sidebar.notificationInvitedBy", { name: invitedByDisplayName })}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      ) : (
        row
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tender.dialogs.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.dialogs.deleteDescription", { title: tenderCase.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("tender.dialogs.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => onDelete(tenderCase.id)}
            >
              {t("tender.landing.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={failedWarningOpen} onOpenChange={setFailedWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("tender.landing.failedSubcategoriesConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.landing.failedSubcategoriesConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("tender.dialogs.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setFailedWarningOpen(false)
                // Defer to next tick — opening a second Radix modal in the same
                // handler that closes this one can race its exit cleanup and
                // leave the handoff dialog unable to open (stuck pointer-events
                // lock on body).
                window.setTimeout(() => setHandoffOpen(true), 0)
              }}
            >
              {t("tender.landing.failedSubcategoriesConfirmContinue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TenderHandoffDialog
        caseId={tenderCase.id}
        handedOff={isHandedOff(tenderCase)}
        open={handoffOpen}
        responsible={
          tenderCase.responsibleUserOid
            ? {
                oid: tenderCase.responsibleUserOid,
                displayName: tenderCase.responsibleDisplayName ?? "",
                email: tenderCase.responsibleEmail ?? "",
              }
            : null
        }
        onOpenChange={setHandoffOpen}
      />
    </>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function TenderLandingPage() {
  const { t, i18n } = useTranslation()
  const settingsService = SettingsService.getInstance()
  const navigate = useNavigate()
  const { routes } = useRoutes()
  const { casePath } = useTenderPaths()
  const { pathname } = useLocation()
  // Admin mode is opt-in: membership only offers the toggle; the elevated landing
  // affordances (all-cases banner, owner column, group-by-owner) appear only when
  // the admin has switched it on. Backend access (admin_default) is gated the same
  // way via the request header, so the UI and data stay consistent.
  const isTenderAdminMember = settingsService.settings.user_policy.is_tender_admin
  const adminModeEnabled = useTenderAdminModeStore((s) => s.enabled)
  const isTenderAdmin = isTenderAdminMember && adminModeEnabled
  const cvLibraryKeyword =
    routes.find((r) => pathname.startsWith(r.path))?.overrides?.cvLibraryKeyword ?? "cv"
  const { data: cases = [], isLoading, isError, refetch, isRefetching } = useTenderCases()
  const deleteCase = useDeleteTenderCase()
  const deleteCases = useDeleteTenderCases()
  const [sortKey, setSortKey] = useState<SortKey>("activity")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const [query, setQuery] = useState("")
  // Default to the user's own involved cases; "All cases" reveals the open org-wide
  // list. Each filter restores the user's last choice from localStorage.
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>(initialOwnerFilter)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatusFilter)
  const [handoffFilter, setHandoffFilter] = useState<HandoffFilter>(initialHandoffFilter)

  // Persist filter choices so the chosen view is remembered across visits.
  useEffect(() => {
    // Only persist the mine/all sentinels for the owner filter (see initialOwnerFilter).
    if (ownerFilter === OWNER_FILTER_MINE || ownerFilter === OWNER_FILTER_ALL) {
      writeStoredFilter(FILTER_STORAGE_KEYS.owner, ownerFilter)
    }
  }, [ownerFilter])
  useEffect(() => {
    writeStoredFilter(FILTER_STORAGE_KEYS.status, statusFilter)
  }, [statusFilter])
  useEffect(() => {
    writeStoredFilter(FILTER_STORAGE_KEYS.handoff, handoffFilter)
  }, [handoffFilter])
  // The per-owner filter options only exist in admin mode. If the admin turns the
  // toggle off (isTenderAdmin → false) while a specific owner is selected, the
  // menu items vanish but the selection would otherwise persist with no way to
  // clear it — reset it back to "My cases".
  useEffect(() => {
    if (!isTenderAdmin && ownerFilter !== OWNER_FILTER_ALL && ownerFilter !== OWNER_FILTER_MINE) {
      setOwnerFilter(OWNER_FILTER_MINE)
    }
  }, [isTenderAdmin, ownerFilter])
  const [groupByOwner, setGroupByOwner] = useState(false)
  const [collapsedOwnerGroups, setCollapsedOwnerGroups] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Drop selected cases that are no longer listed (deleted from their row or by
  // someone else), so a bulk delete never targets a case that is gone.
  useEffect(() => {
    setSelectedIds((prev) => {
      const listed = new Set(cases.map((c) => c.id))
      const kept = [...prev].filter((id) => listed.has(id))
      return kept.length === prev.size ? prev : new Set(kept)
    })
  }, [cases])
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const [quickStartOpen, setQuickStartOpen] = useState(false)

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  const handleQueryChange = (value: string) => {
    setQuery(value)
    setSelectedIds(new Set())
  }

  const unknownOwnerLabel = t("tender.landing.unknownOwner")

  const ownerOptions = useMemo(
    () =>
      [...new Set(cases.map((tenderCase) => getOwnerDisplayName(tenderCase, unknownOwnerLabel)))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, i18n.language)),
    [cases, i18n.language, unknownOwnerLabel]
  )

  let ownerFilterLabel = ownerFilter
  if (ownerFilter === OWNER_FILTER_ALL) {
    ownerFilterLabel = t("tender.landing.filterAllCases")
  } else if (ownerFilter === OWNER_FILTER_MINE) {
    ownerFilterLabel = t("tender.landing.filterMine")
  }

  const ownerFilteredCases = useMemo(() => {
    if (ownerFilter === OWNER_FILTER_ALL) {
      return cases
    }

    // "My cases" = cases the current user is actively involved in (owner or participant).
    if (ownerFilter === OWNER_FILTER_MINE) {
      return cases.filter(isInvolvedCase)
    }

    // Admin-only: filter by a specific owner's display name.
    return cases.filter(
      (tenderCase) => getOwnerDisplayName(tenderCase, unknownOwnerLabel) === ownerFilter
    )
  }, [cases, ownerFilter, unknownOwnerLabel])

  const statusFilteredCases = useMemo(
    () => ownerFilteredCases.filter((tenderCase) => matchesStatusFilter(tenderCase, statusFilter)),
    [ownerFilteredCases, statusFilter]
  )

  const handoffFilteredCases = useMemo(() => {
    if (handoffFilter === HANDOFF_FILTER_ALL) return statusFilteredCases
    // The split is the extraction boundary, not just the explicit handoff flag:
    // anything past extraction (CV-match/bid-review started, Go/No-Go, or handed
    // off) is post-prep, so it never shows under "Under förberedelse".
    const wantInPrep = handoffFilter === "in_prep"
    return statusFilteredCases.filter((c) => isInPreparation(c) === wantInPrep)
  }, [statusFilteredCases, handoffFilter])

  const getDerivedStatusLabel = useCallback(
    (item: TenderCaseListItemApi) => t(STATUS_CONFIG[deriveListItemStatus(item)].labelKey),
    [t]
  )
  const filteredCases = useMemo(
    () =>
      filterCases(handoffFilteredCases, query, {
        getDerivedStatusLabel,
        unknownOwnerLabel,
      }),
    [getDerivedStatusLabel, handoffFilteredCases, query, unknownOwnerLabel]
  )
  const sortedCases = sortCases(filteredCases, {
    key: sortKey,
    dir: sortDir,
    locale: i18n.language,
  })
  const displayCases = [...sortedCases].sort((a, b) => {
    const aP = isProcessing(a) ? 0 : 1
    const bP = isProcessing(b) ? 0 : 1
    return aP - bP
  })
  const tableColumnCount = isTenderAdmin ? 9 : 8
  const groupedCases = useMemo(() => {
    if (!isTenderAdmin || !groupByOwner) {
      return []
    }

    const groups = new Map<string, TenderCaseListItemApi[]>()
    for (const tenderCase of displayCases) {
      const ownerName = getOwnerDisplayName(tenderCase, unknownOwnerLabel)
      const current = groups.get(ownerName) ?? []
      current.push(tenderCase)
      groups.set(ownerName, current)
    }

    return [...groups.entries()].sort(([ownerA], [ownerB]) =>
      ownerA.localeCompare(ownerB, i18n.language)
    )
  }, [displayCases, groupByOwner, i18n.language, isTenderAdmin, unknownOwnerLabel])
  const groupByOwnerButtonClass = cn(
    "h-7 gap-1.5 px-2.5 text-xs",
    groupByOwner && "border border-foreground bg-accent text-accent-foreground"
  )
  const visibleCases = useMemo(() => {
    if (!isTenderAdmin || !groupByOwner) {
      return displayCases
    }

    return groupedCases.flatMap(([ownerName, ownerCases]) =>
      collapsedOwnerGroups.has(ownerName) ? [] : ownerCases
    )
  }, [collapsedOwnerGroups, displayCases, groupByOwner, groupedCases, isTenderAdmin])

  const allVisibleSelected =
    visibleCases.length > 0 && visibleCases.every((c) => selectedIds.has(c.id))
  const someVisibleSelected = visibleCases.some((c) => selectedIds.has(c.id))

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const tenderCase of visibleCases) {
          next.delete(tenderCase.id)
        }
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const tenderCase of visibleCases) {
          next.add(tenderCase.id)
        }
        return next
      })
    }
  }

  const toggleOwnerGroup = (ownerName: string) => {
    setCollapsedOwnerGroups((prev) => {
      const next = new Set(prev)
      if (next.has(ownerName)) {
        next.delete(ownerName)
      } else {
        next.add(ownerName)
      }
      return next
    })
  }

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleOpenCase = (id: string) => {
    void navigate(casePath(id))
  }

  const handleBulkDelete = () => {
    deleteCases.mutate([...selectedIds], {
      onSuccess: () => {
        setSelectedIds(new Set())
        setBulkConfirmOpen(false)
      },
      onError: (error) => {
        // Keep only the cases that are left, so a retry skips the deleted ones.
        if (error instanceof TenderCasesDeleteError) setSelectedIds(new Set(error.failedIds))
      },
    })
  }

  const allSelectedAreOwned = [...selectedIds].every((id) => {
    const c = cases.find((tc) => tc.id === id)
    // Same predicate as the row-level delete action: owners and tender admins
    // (admin_default) can delete; open viewers (null role via open-read) cannot.
    return !c || canDeleteCase(c)
  })

  const casesContent = (() => {
    if (isLoading) {
      return (
        <div className="border-border bg-background/60 overflow-hidden rounded-xl border backdrop-blur-sm">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border-b px-5 py-4 last:border-0">
              <Skeleton className="h-4 w-1/3" />
            </div>
          ))}
        </div>
      )
    }
    // No list at all (the response could not be read): say so rather than
    // showing an empty list that reads as "you have no tenders".
    if (isError && cases.length === 0) {
      return (
        <div
          className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-xl border px-5 py-8 text-center"
          role="alert"
        >
          <AlertTriangle className="text-destructive h-5 w-5" />
          <p className="text-foreground text-sm">{t("tender.landing.casesLoadError")}</p>
          <Button
            disabled={isRefetching}
            size="sm"
            variant="outline"
            onClick={() => void refetch()}
          >
            {t("tender.landing.casesLoadRetry")}
          </Button>
        </div>
      )
    }
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-muted-foreground shrink-0 text-xs font-semibold tracking-wide uppercase">
              {query
                ? t("tender.landing.casesHeadingFiltered", {
                    filtered: filteredCases.length,
                    total: cases.length,
                  })
                : t("tender.landing.casesHeading", { count: cases.length })}
            </h2>
            {/* Primary view switch: preparation phase vs after handoff. */}
            {cases.length > 0 && (
              <div className="bg-muted/40 inline-flex items-center gap-1 rounded-lg border p-1">
                {HANDOFF_SEGMENTS.map((segment) => (
                  <button
                    key={segment.value}
                    type="button"
                    className={cn(
                      "h-7 rounded-md px-3 text-xs font-medium transition-colors",
                      handoffFilter === segment.value
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => setHandoffFilter(segment.value)}
                  >
                    {t(segment.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {cases.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* Mine / All cases filter — available to everyone; per-owner options are admin-only. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-7 gap-1.5 px-2.5 text-xs" size="sm" variant="outline">
                    <Users className="size-3.5" />
                    {ownerFilterLabel}
                    <ChevronDown className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-72 w-56 overflow-y-auto">
                  <DropdownMenuItem onClick={() => setOwnerFilter(OWNER_FILTER_MINE)}>
                    <Check
                      className={cn(
                        "mr-2 size-3.5",
                        ownerFilter === OWNER_FILTER_MINE ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {t("tender.landing.filterMine")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOwnerFilter(OWNER_FILTER_ALL)}>
                    <Check
                      className={cn(
                        "mr-2 size-3.5",
                        ownerFilter === OWNER_FILTER_ALL ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {t("tender.landing.filterAllCases")}
                  </DropdownMenuItem>
                  {isTenderAdmin && ownerOptions.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      {ownerOptions.map((ownerName) => (
                        <DropdownMenuItem key={ownerName} onClick={() => setOwnerFilter(ownerName)}>
                          <Check
                            className={cn(
                              "mr-2 size-3.5",
                              ownerFilter === ownerName ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="truncate">{ownerName}</span>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Stage / status filter — available to everyone. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-7 gap-1.5 px-2.5 text-xs" size="sm" variant="outline">
                    <Scale className="size-3.5" />
                    {statusFilter === STATUS_FILTER_ALL
                      ? t("tender.landing.filterAllStatuses")
                      : t(
                          STATUS_FILTER_BUCKETS.find((b) => b.value === statusFilter)?.labelKey ??
                            "tender.landing.filterAllStatuses"
                        )}
                    <ChevronDown className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-72 w-56 overflow-y-auto">
                  <DropdownMenuItem onClick={() => setStatusFilter(STATUS_FILTER_ALL)}>
                    <Check
                      className={cn(
                        "mr-2 size-3.5",
                        statusFilter === STATUS_FILTER_ALL ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {t("tender.landing.filterAllStatuses")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {STATUS_FILTER_BUCKETS.map((bucket) => (
                    <DropdownMenuItem
                      key={bucket.value}
                      onClick={() => setStatusFilter(bucket.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 size-3.5",
                          statusFilter === bucket.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {t(bucket.labelKey)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {isTenderAdmin && (
                <Button
                  className={groupByOwnerButtonClass}
                  size="sm"
                  variant="outline"
                  onClick={() => setGroupByOwner((value) => !value)}
                >
                  <Users className="size-3.5" />
                  {groupByOwner
                    ? t("tender.landing.ungroupByOwner")
                    : t("tender.landing.groupByOwner")}
                </Button>
              )}

              <div className="relative w-64">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
                <Input
                  className="h-7 pr-7 pl-8 text-xs"
                  placeholder={t("tender.landing.searchPlaceholder")}
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                />
                {query && (
                  <Button
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 size-7 -translate-y-1/2 p-0"
                    size="icon"
                    type="button"
                    variant="ghost"
                    onClick={() => handleQueryChange("")}
                  >
                    <X className="size-3.5" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-border bg-background/60 overflow-x-auto rounded-xl border backdrop-blur-sm">
          <Table className="min-w-[860px] border-collapse" parentClassName="overflow-visible">
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50 border-b">
                <TableHead className="w-10 px-3 py-2.5">
                  <div
                    className={cn(
                      "flex items-center justify-center transition-opacity duration-150",
                      someVisibleSelected ? "opacity-100" : "opacity-0"
                    )}
                  >
                    <Checkbox
                      checked={allVisibleSelected || (someVisibleSelected && "indeterminate")}
                      className="cursor-pointer"
                      onCheckedChange={() => toggleSelectAll()}
                    />
                  </div>
                </TableHead>
                <SortableHeader
                  active={sortKey}
                  className="px-5 text-left"
                  dir={sortDir}
                  label={t("tender.landing.columnCase")}
                  sortKey="title"
                  onSort={handleSort}
                />
                {isTenderAdmin && (
                  <TableHead className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium tracking-wide uppercase">
                    {t("tender.landing.columnOwner")}
                  </TableHead>
                )}
                <TableHead className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium tracking-wide uppercase">
                  {t("tender.landing.columnStatus")}
                </TableHead>
                <SortableHeader
                  active={sortKey}
                  dir={sortDir}
                  label={t("tender.landing.columnLastActivity")}
                  sortKey="activity"
                  onSort={handleSort}
                />
                <TableHead className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium tracking-wide uppercase">
                  {t("tender.landing.columnDocs")}
                </TableHead>
                <SortableHeader
                  active={sortKey}
                  dir={sortDir}
                  label={t("tender.landing.columnDeadline")}
                  sortKey="deadline"
                  onSort={handleSort}
                />
                <SortableHeader
                  active={sortKey}
                  dir={sortDir}
                  label={t("tender.landing.columnCreated")}
                  sortKey="createdAt"
                  onSort={handleSort}
                />
                <TableHead className="w-24 px-4 py-2.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayCases.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    className="text-muted-foreground px-5 py-8 text-center text-sm"
                    colSpan={tableColumnCount}
                  >
                    {query
                      ? t("tender.landing.noResultsForQuery", { query })
                      : t("tender.landing.noCasesInList")}
                  </TableCell>
                </TableRow>
              )}
              {displayCases.length > 0 &&
                isTenderAdmin &&
                groupByOwner &&
                groupedCases.map(([ownerName, ownerCases]) => (
                  <Fragment key={ownerName}>
                    <TableRow className="bg-muted/30 hover:bg-muted/30 border-y">
                      <TableCell className="px-5 py-2.5" colSpan={tableColumnCount}>
                        <div className="flex items-center gap-2">
                          <Button
                            className="hover:bg-accent inline-flex h-auto items-center gap-2 px-1 py-0.5"
                            type="button"
                            variant="ghost"
                            onClick={() => toggleOwnerGroup(ownerName)}
                          >
                            {collapsedOwnerGroups.has(ownerName) ? (
                              <ChevronRight className="size-3.5" />
                            ) : (
                              <ChevronDown className="size-3.5" />
                            )}
                            <span className="text-sm font-medium">{ownerName}</span>
                          </Button>
                          <Badge className="text-[10px]" variant="secondary">
                            {ownerCases.length}
                          </Badge>
                        </div>
                      </TableCell>
                    </TableRow>
                    {!collapsedOwnerGroups.has(ownerName) &&
                      ownerCases.map((tenderCase) => {
                        return (
                          <CaseRow
                            key={tenderCase.id}
                            isSelected={selectedIds.has(tenderCase.id)}
                            isSelectionMode={selectedIds.size > 0}
                            showHandoffBadge={handoffFilter === HANDOFF_FILTER_ALL}
                            showOwnerColumn={isTenderAdmin}
                            tenderCase={tenderCase}
                            onClick={handleOpenCase}
                            onDelete={(id) => deleteCase.mutate(id)}
                            onToggle={() => toggleSelectOne(tenderCase.id)}
                          />
                        )
                      })}
                  </Fragment>
                ))}
              {displayCases.length > 0 &&
                (!isTenderAdmin || !groupByOwner) &&
                displayCases.map((tenderCase) => {
                  return (
                    <CaseRow
                      key={tenderCase.id}
                      isSelected={selectedIds.has(tenderCase.id)}
                      isSelectionMode={selectedIds.size > 0}
                      showHandoffBadge={handoffFilter === HANDOFF_FILTER_ALL}
                      showOwnerColumn={isTenderAdmin}
                      tenderCase={tenderCase}
                      onClick={handleOpenCase}
                      onDelete={(id) => deleteCase.mutate(id)}
                      onToggle={() => toggleSelectOne(tenderCase.id)}
                    />
                  )
                })}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  })()

  return (
    <>
      <SidebarInset className="relative flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full flex-col overflow-hidden">
        {/* Background gradients */}
        <div className="from-secondary/20 via-secondary/8 to-secondary/3 pointer-events-none absolute inset-0 bg-gradient-to-tl" />
        <div className="from-secondary/15 via-secondary/8 pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_80%_80%,_var(--tw-gradient-stops))] to-transparent" />

        <main className="relative flex-1 overflow-y-auto">
          {/* Hero */}
          <div className="relative flex flex-col items-center px-6 pt-10 pb-8 text-center">
            <div className="bg-primary/10 mb-5 flex size-12 items-center justify-center rounded-lg">
              <Scale className="text-primary size-6" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">{t("tender.module.title")}</h1>
            <p className="text-muted-foreground mt-1.5 max-w-md text-sm">
              {t("tender.module.subtitle")}
            </p>
            <Button
              className="mt-5"
              disabled={isLoading}
              size="default"
              onClick={() => setQuickStartOpen(true)}
            >
              <Plus className="mr-2 size-4" />
              {t("tender.actions.newCase")}
            </Button>

            <TenderAdminModeToggle variant="banner" />
          </div>

          {/* Cases section — fills the available width so the wide table doesn't
              need a horizontal scroll while space sits unused beside it. */}
          <div className="relative px-6 pb-16">
            <div className="mx-auto w-full max-w-screen-2xl">{casesContent}</div>
          </div>
        </main>
      </SidebarInset>

      {selectedIds.size > 0 && (
        <div className="border-border/60 bg-background/95 animate-in slide-in-from-bottom-3 fade-in fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2.5 rounded-full border px-5 py-2.5 shadow-lg backdrop-blur-md duration-200">
          <span className="text-sm font-medium tabular-nums">
            {selectedIds.size === 1
              ? t("tender.landing.selectedCountOne")
              : t("tender.landing.selectedCountMany", { count: selectedIds.size })}
          </span>
          <div className="bg-border mx-1 h-4 w-px" />
          <Button
            className="h-7 rounded-full px-3 text-xs"
            disabled={deleteCases.isPending || !allSelectedAreOwned}
            size="sm"
            variant="destructive"
            title={
              !allSelectedAreOwned ? t("tender.landing.bulkDeleteOwnedOnlyTooltip") : undefined
            }
            onClick={() => setBulkConfirmOpen(true)}
          >
            <Trash2 className="mr-1.5 size-3" />
            {t("tender.landing.remove")}
          </Button>
          <Button
            aria-label={t("tender.landing.clearSelectionAria")}
            className="text-muted-foreground hover:text-foreground ml-0.5 size-7 rounded-full p-0"
            size="icon"
            type="button"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedIds.size === 1
                ? t("tender.landing.bulkDeleteOne")
                : t("tender.landing.bulkDeleteMany", { count: selectedIds.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.landing.bulkDeleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCases.isPending}>
              {t("tender.landing.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCases.isPending}
              onClick={handleBulkDelete}
            >
              {t("tender.landing.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuickStartModal
        cvLibraryKeyword={cvLibraryKeyword}
        open={quickStartOpen}
        onClose={() => setQuickStartOpen(false)}
      />
    </>
  )
}
